import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { GuidedSearchSetting } from '@interfaces/guided-search-setting.model';
import { MediaObserver } from '@ngbracket/ngx-layout';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { GuidedSearchService } from '@services/guided-search/guided-search.service';
import { setSearchMethodAction } from '@store/search/search.actions';
import { SearchMethods } from '@enums/search-methods.enum';
import { select, Store } from '@ngrx/store';
import { SearchState } from '@store/search/search.state';
import { CardEmphasis } from '@zelis/dls/card';
import { StorageUtilities } from '@utilities/storage.utilities';
import { AuthStatus, SubscriptionManager } from '@zelis/platform-ui-components';
import { AuthStoreSelectors } from '@store/auth';

@Component({
  selector: 'app-guided-search-tile',
  templateUrl: './guided-search-tile.component.html',
  styleUrls: ['./guided-search-tile.component.scss'],
})
export class GuidedSearchTileComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() category?: GuidedSearchSetting;
  @Input() cardIndex = 0;
  @Input() smartshopper?: boolean = false;
  @Input() showAllCategories?: boolean = false;
  @Input() loading?: boolean = false;

  @ViewChild('title') elementRef: ElementRef;

  public iconSize: string = 'lg';
  public selectedCategory: GuidedSearchSetting;
  public smartshopperEmphasis: CardEmphasis = {
    position: 'top',
    color: 'system-green',
  };

  private mediaSubscription: Subscription;
  private subscriptions = new SubscriptionManager();
  private auth: AuthStatus = new AuthStatus();
  private procedureCostTileNames: string[] = [
    'guided_search_cost_findCost_top',
    'guided_search_cost_header',
    'guided_search_cost_title',
    'guided_search_header_cost_medical',
    'guided_search_home_findCost_header',
    'guided_search_medicalProcedures_header',
    'guided_search_top_cost',
    'guided_search_top_cost_header',
    'guided_search_top_medicalProcedures',
    'wayfinding_cost_findCost_title',
    'wayfinding_home_findCost_header',
    'wayfinding_pages_findCost_title',
  ];

  constructor(
    private guidedSearchService: GuidedSearchService,
    private mediaQuery: MediaObserver,
    private store: Store<SearchState>,
    private storage: StorageUtilities = new StorageUtilities()
  ) {}

  ngOnInit() {
    this.subscribeToAuth();
    if (
      this.storage.sessionStorageGet('guidedSearchPrompt') &&
      this.auth.auth_status &&
      this.procedureCostTileNames.includes(this.category?.name)
    ) {
      this.onTileClick(this.category);
      this.removeGuidedSearchPrompt();
    }
  }

  ngAfterViewInit() {
    this.resizeTitle();
    this.mediaSubscription = this.subscribeToMediaChange();
  }

  ngOnDestroy() {
    this.mediaSubscription.unsubscribe();
    this.subscriptions.destroy();
  }

  public onTileClick(option: GuidedSearchSetting): void {
    const cardOrder = this.cardIndex + 1;

    this.store.dispatch(
      setSearchMethodAction({ searchMethod: SearchMethods.BrowseByCategory })
    );
    this.guidedSearchService.selectGuidedSearchOption(option, true, cardOrder);
  }

  private resizeTitle(): void {
    if (
      this.mediaQuery.isActive('gt-sm') &&
      !this.loading &&
      !this.showAllCategories &&
      this.elementRef.nativeElement.offsetHeight > 56
    ) {
      this.elementRef.nativeElement.classList.add('clamp', 'resize');
    }
  }

  private subscribeToMediaChange(): Subscription {
    return this.mediaQuery
      .asObservable()
      .pipe(filter(() => this.mediaQuery.isActive('gt-sm')))
      .subscribe(() => this.resizeTitle());
  }

  private subscribeToAuth(): void {
    this.subscriptions.add(
      this.store
        .pipe(select(AuthStoreSelectors.getAuthStatus))
        .subscribe((data) => (this.auth = data))
    );
  }

  private removeGuidedSearchPrompt(): void {
    this.storage.sessionStorageRemove('guidedSearchPrompt');
  }
}
